import { useSubscription } from './useSubscription';
import {
  FeatureKey,
  canAccessFeatureByEntitlements,
  getFeaturesForEntitlements,
} from '../config/featureAccess';
import { hasEntitlement as hasEntitlementForPlan } from '../config/entitlements';
import type { EntitlementKey } from '../config/entitlements';
import useAuthUser from '../services/useAuthUser';

export function useFeatureAccess() {
  const subscription = useSubscription();
  const { user } = useAuthUser();

  /** Case 1 = sample (no auth). Cases 2–17 = free with sign-in only. */
  const isRedRockPracticeCase = (f: FeatureKey) => f.startsWith('case') && f !== 'case1';

  return {
    ...subscription,
    canAccess: (feature: FeatureKey): boolean => {
      if (feature === 'case1') return true;
      if (isRedRockPracticeCase(feature)) return !!user;
      if (!user) return false;
      if (!subscription.isSubscriptionActive) return false;
      return canAccessFeatureByEntitlements(feature, subscription.entitlements);
    },
    // Check if user has a canonical entitlement (plan-based)
    hasEntitlement: (entitlementKey: EntitlementKey): boolean => {
      if (!user) return false;
      if (!subscription.isSubscriptionActive) return false;
      return hasEntitlementForPlan(subscription.planId ?? subscription.data?.plan, entitlementKey);
    },
    availableFeatures: (): FeatureKey[] => {
      if (!user || !subscription.isSubscriptionActive) {
        return getFeaturesForEntitlements([]);
      }
      return getFeaturesForEntitlements(subscription.entitlements);
    },
    canAccessAny: (features: FeatureKey[]): boolean => {
      if (features.some(f => f === 'case1')) return true;
      if (features.some(isRedRockPracticeCase)) return !!user;
      if (!user) return false;
      if (!subscription.isSubscriptionActive) return false;
      return features.some(feature => canAccessFeatureByEntitlements(feature, subscription.entitlements));
    },
    canAccessAll: (features: FeatureKey[]): boolean => {
      if (features.length === 1 && features[0] === 'case1') return true;
      if (features.every(isRedRockPracticeCase)) return !!user;
      if (!user) return false;
      if (!subscription.isSubscriptionActive) return false;
      return features.every(feature => canAccessFeatureByEntitlements(feature, subscription.entitlements));
    }
  };
} 
